import { useState } from 'react';

import type { CheckboxButtonGroupOption } from '@capsule/consumer-design-system';
import { ScreenReaderOnly, CheckboxButtonGroupVariant } from '@capsule/consumer-design-system';

import { Form, Formik } from 'formik';
import { useHistory } from 'react-router';
import styled from 'styled-components';

import { SimpleFeatureLayout } from 'src/app/components/FeatureLayouts/SimpleFeatureLayout/SimpleFeatureLayout';
import { FormikCheckboxButtonGroup } from 'src/app/components/Form/FormikCheckboxButtonGroup/FormikCheckboxButtonGroup';
import { FormikTextField } from 'src/app/components/Form/FormikTextField/FormikTextField';
import { EscriptOnboardingStepName } from 'src/app/components/Pages/EscriptOnboarding/types';
import { oxfordCommaFormatter, persistedFormValue } from 'src/app/components/Pages/EscriptOnboarding/utils';
import { usePatientList, usePatientUpdate } from 'src/app/hooks/data/patient';
import { useUser } from 'src/app/hooks/data/user';
import useAuth from 'src/app/hooks/useAuth';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { newOnboardingStep } from 'src/app/router/paths';
import { ESCRIPT_ONBOARDING_ALLERGIES_FORM } from 'src/app/storage/localStorageKeys';
import { getPrimaryPatientFromList } from 'src/app/utils/data/patient';

const allergyOptions = [
  'Penicillin',
  'Sulfa',
  'Amoxicillin',
  'Seasonal',
  'Shellfish',
  'Pollen',
  'Codeine',
  'Gluten',
  'Latex',
  'Aspirin',
] as const;

const allergyCheckboxGroupOptions: Pick<CheckboxButtonGroupOption, 'label' | 'value'>[] = allergyOptions.map(
  allergy => ({
    label: allergy,
    value: allergy,
  })
);

type AllergyFormValues = {
  [key in typeof allergyOptions[number]]: boolean;
} & { other_allergies: string };

const allergyFormInitialFormValues: AllergyFormValues = {
  ...Object.assign({}, ...allergyOptions.map(allergy => ({ [allergy]: false }))),
  other_allergies: '',
};

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.s4};
`;

/** We use empty spaces to create a truthy value that is renderless for errors in this form. */
const EMPTY_SPACE = ' ';
const FORM_ID = 'allergyForm';

export const EscriptOnboardingAllergiesStep = () => {
  useSegmentEvent('viewCheckoutAllergyProfile', { shouldTrackOnMount: true });
  const analyticsDispatchSaveAllergyProfile = useSegmentEvent('saveCheckoutAllergyProfile', {
    shouldTrackOnMount: false,
  });

  const history = useHistory();
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const { initialValue, persistValue, clearPersistedValue } = persistedFormValue(
    ESCRIPT_ONBOARDING_ALLERGIES_FORM,
    allergyFormInitialFormValues
  );

  const { loggedInUser } = useAuth();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', Boolean(loggedInUser));
  const accountId = user?.accountId ?? '';
  const { data: patients } = usePatientList(parseInt(accountId), Boolean(accountId));

  const primaryPatient = patients ? getPrimaryPatientFromList(patients) : null;

  const { mutateAsync: mutatePatientAsync } = usePatientUpdate(
    primaryPatient ? parseInt(primaryPatient.id) : -1,
    accountId ? parseInt(accountId) : -1
  );

  return (
    <Formik
      initialValues={initialValue}
      validateOnBlur={hasSubmitted}
      validateOnChange={hasSubmitted}
      validate={formValue => {
        const hasTruthyValue = Object.values(formValue).some(val => !!val === true);

        // no errors should render if some input has value!
        if (hasTruthyValue) return {};

        const allFieldsErrored = Object.assign(
          {},
          ...Object.keys(allergyFormInitialFormValues).map(keyName => ({ [keyName]: EMPTY_SPACE }))
        );

        /**
         * This error object contains an error value for a field called `form`. The
         * individual fields will be in an error state without a helpful error message,
         * but `errors.form` should be leveraged somewhere else to properly announce
         * relevant form errors.
         */
        return {
          ...allFieldsErrored,
          form: (
            <>
              Please make a selection or select ‘No Known Allergies’
              <ScreenReaderOnly>
                . To select ‘No Known Allergies’, progress to the next focusable element.
              </ScreenReaderOnly>
            </>
          ),
        };
      }}
      onSubmit={async formValue => {
        const { other_allergies: textInputValue, ...optionValues } = formValue;

        const selectedOptions = allergyOptions.filter(allergy => !!optionValues[allergy]);

        const formattedString = oxfordCommaFormatter(
          textInputValue ? [...selectedOptions, textInputValue] : selectedOptions
        );

        const allergies = formattedString === '' ? 'None' : formattedString;

        await mutatePatientAsync({ allergies, medicalConditions: 'None' });
        analyticsDispatchSaveAllergyProfile({
          allergies,
          allergiesToggle: 'Yes',
          conditions: null,
          conditionsToggle: null,
          medications: null,
          medicationsToggle: null,
          doctors: null,
        });

        persistValue(formValue);
        history.push(newOnboardingStep.toPath({ stepName: EscriptOnboardingStepName.other_meds }));
      }}
    >
      {({ errors, isSubmitting, resetForm, values, setSubmitting }) => {
        const hasFormValueSomewhere = Object.values(values).some(val => !!val === true);
        const isSubmittingForm = hasFormValueSomewhere && isSubmitting;
        const isSkippingForm = !hasFormValueSomewhere && isSubmitting;

        return (
          <SimpleFeatureLayout
            keyName={EscriptOnboardingStepName.allergies}
            title={`Do you have any allergies?`}
            description={`This helps us keep you safe. You can also type any allergies at the bottom.`}
            body={{
              content: (
                <StyledForm as={Form} noValidate id={FORM_ID}>
                  <FormikCheckboxButtonGroup
                    label={{ content: 'Mark all relevant options.', isHidden: true }}
                    options={allergyCheckboxGroupOptions}
                    variant={CheckboxButtonGroupVariant.MultiColumnFixed}
                  />
                  <FormikTextField
                    name="other_allergies"
                    label={{ content: `Other allergies (please list)`, isHidden: false }}
                  />
                </StyledForm>
              ),
            }}
            cta1={{
              children: 'Submit',
              isDisabled: isSkippingForm,
              isSubmitting: isSubmittingForm,
              form: FORM_ID,
              onClick: () => setHasSubmitted(true),
            }}
            cta2={{
              children: 'No known allergies',
              isDisabled: isSubmittingForm,
              isLoading: isSkippingForm,
              onClick: async () => {
                resetForm({ values: allergyFormInitialFormValues });
                setSubmitting(true);

                //await dispatch(updatePrimaryPatientInfo({ allergies: 'None', medicalConditions: 'None' }));
                await mutatePatientAsync({ allergies: 'None', medicalConditions: 'None' });
                analyticsDispatchSaveAllergyProfile({
                  allergies: 'None',
                  allergiesToggle: 'No',
                  conditions: null,
                  conditionsToggle: null,
                  medications: null,
                  medicationsToggle: null,
                  doctors: null,
                });

                clearPersistedValue();
                history.push(newOnboardingStep.toPath({ stepName: EscriptOnboardingStepName.other_meds }));
              },
            }}
            // @ts-expect-error - There _is_ a type error here, but we're ignoring it because it's the simplest way to express top-level errors.
            error={errors?.form}
          />
        );
      }}
    </Formik>
  );
};
